<template>
  <div class="m-card">
    <div class="m-card__header">
      <div class="title-c">{{ threshold.label }}<MetricInfoTooltip :content="helpText" /></div>
      <CardArrowLink v-if="url" :to="url" />
    </div>
    <div class="m-card__main">
      <div class="letter">{{ display }}</div>
      <div v-if="tag">
        <div class="tag-number" :class="tag.color">{{ tag.copy }}</div>
      </div>
    </div>
    <div class="m-card__footer" :class="footer.color">
      <div>{{ footer.copy }}</div>
    </div>
  </div>
</template>

<script>
import CardArrowLink from '../CardArrowLink';
import MetricInfoTooltip from '@/components/Levels/MetricInfoTooltip';
import { buildOnDemandMetricHelpText } from '@/utils/onDemandLevelHelpTexts';

const METRIC_RATING = 'rating';
const METRIC_INCIDENTS = 'incidents';
const DIRECTION_LOWER_IS_BETTER = 'lower_is_better';
const TIER_GOOD = 'good';
const TIER_MIDDLE = 'middle';
const TIER_BAD = 'bad';
const TAG_GOOD = 'gray';
const TAG_MIDDLE = 'orange';
const TAG_BAD = 'red';
const FOOTER_GOOD = 'green';
const FOOTER_MIDDLE = 'orange';
const FOOTER_BAD = 'red';
const FOOTER_NEUTRAL = '';
const COPY_GOOD = 'Continúa así';
const COPY_MIDDLE = 'No bajes de este nivel';
const COPY_BAD = 'Está afectando tu nivel';
const COPY_NO_DATA = 'Sin datos';
const COPY_NO_ORDERS = 'sin pedidos';
const COPY_NO_REVIEWS = 'sin reseñas suficientes';
const COPY_MISSING = 'sin datos';
const INCIDENTS_TAG_COPIES = {
  [TIER_GOOD]: 'Pocas incidencias',
  [TIER_MIDDLE]: 'Más incidencias de lo ideal',
  [TIER_BAD]: 'Demasiadas incidencias'
};
const NO_DATA_DISPLAY = '—';
const RATING_SCALE_MAX = 5;
const DECIMALS = 2;
const PERCENT_DECIMALS = 0;
const RATING_DECIMALS = 1;
const INCIDENTS_DECIMALS = 4;
const INCIDENTS_DISPLAY_DECIMALS = 3;
const METRIC_TO_LEVEL_SCALE = 3;
const TOP_CUTOFF = 0;
const NOT_FOUND = -1;
const EMPTY = 0;
const SINGLE = 1;
const NO_ORDERS = 0;

const METRIC_FIELDS = {
  incidents: { score: 'incident_score', value: 'incident_ratio' },
  availability: { score: 'availability_score', value: 'availability_pct' },
  active_menu: { score: 'active_menu_score', value: 'active_menu_pct' },
  rating: { score: 'rating_score', value: 'rating_average' }
};

const METRIC_ROUTES = {
  incidents: '/foodcourt/claims',
  availability: '/foodcourt/restaurantes',
  active_menu: '/foodcourt/carta-disponibilidad',
  rating: '/foodcourt/feedback'
};

export default {
  name: 'MetricCard',
  components: { CardArrowLink, MetricInfoTooltip },
  props: {
    threshold: { type: Object, required: true },
    period: { type: Object, default: null },
    cutoffs: { type: Array, default: () => [] }
  },
  computed: {
    helpText() {
      return buildOnDemandMetricHelpText(this.threshold);
    },
    fields() {
      return METRIC_FIELDS[this.threshold.metric] || null;
    },
    url() {
      return METRIC_ROUTES[this.threshold.metric] || null;
    },
    score() {
      if (this.period === null || this.fields === null) {
        return null;
      }
      const value = this.period[this.fields.score];

      return value === undefined ? null : value;
    },
    hasScore() {
      return this.score !== null;
    },
    tier() {
      const sorted = [...this.cutoffs].sort(
        (first, second) => Number(second.minimum_score) - Number(first.minimum_score)
      );

      if (sorted.length === EMPTY) {
        return TIER_MIDDLE;
      }

      const onLevelScale = Number(this.score) * METRIC_TO_LEVEL_SCALE;
      const index = sorted.findIndex(
        cutoff => onLevelScale >= Number(cutoff.minimum_score)
      );

      if (index === TOP_CUTOFF) {
        return TIER_GOOD;
      }
      if (index === NOT_FOUND || index === sorted.length - SINGLE) {
        return TIER_BAD;
      }

      return TIER_MIDDLE;
    },
    display() {
      return this.hasScore
        ? this.formatMeasured(this.period[this.fields.value])
        : NO_DATA_DISPLAY;
    },
    tag() {
      if (!this.hasScore) {
        return null;
      }

      if (this.threshold.metric === METRIC_INCIDENTS) {
        return { color: this.tagColor(), copy: INCIDENTS_TAG_COPIES[this.tier] };
      }

      const lowerIsBetter = this.threshold.direction === DIRECTION_LOWER_IS_BETTER;
      const full = this.formatThreshold(this.threshold.full_score_at);
      const zero = this.formatThreshold(this.threshold.zero_score_at);

      if (this.tier === TIER_GOOD) {
        return {
          color: TAG_GOOD,
          copy: lowerIsBetter ? `Ideal: ${full} o menos` : `Ideal: ${full} o más`
        };
      }
      if (this.tier === TIER_BAD) {
        return {
          color: TAG_BAD,
          copy: lowerIsBetter ? `Pasaste el ${zero} permitido` : `Estás debajo de ${zero}`
        };
      }

      return {
        color: TAG_MIDDLE,
        copy: lowerIsBetter ? `No pases de ${zero}` : `No bajes de ${zero}`
      };
    },
    footer() {
      if (this.period === null) {
        return { color: FOOTER_NEUTRAL, copy: COPY_NO_DATA };
      }
      if (!this.hasScore) {
        return { color: FOOTER_NEUTRAL, copy: this.missingReason() };
      }

      return { color: this.footerColor(), copy: this.footerCopy() };
    }
  },
  methods: {
    tagColor() {
      if (this.tier === TIER_GOOD) {
        return TAG_GOOD;
      }
      if (this.tier === TIER_BAD) {
        return TAG_BAD;
      }

      return TAG_MIDDLE;
    },
    footerColor() {
      if (this.tier === TIER_GOOD) {
        return FOOTER_GOOD;
      }
      if (this.tier === TIER_BAD) {
        return FOOTER_BAD;
      }

      return FOOTER_MIDDLE;
    },
    footerCopy() {
      if (this.tier === TIER_GOOD) {
        return COPY_GOOD;
      }
      if (this.tier === TIER_BAD) {
        return COPY_BAD;
      }

      return COPY_MIDDLE;
    },
    missingReason() {
      if (Number(this.period.orders_count) === NO_ORDERS) {
        return COPY_NO_ORDERS;
      }
      if (this.threshold.metric === METRIC_RATING) {
        return COPY_NO_REVIEWS;
      }

      return COPY_MISSING;
    },
    formatMeasured(value) {
      if (this.threshold.metric === METRIC_INCIDENTS) {
        return Number(value).toFixed(INCIDENTS_DISPLAY_DECIMALS);
      }
      if (this.threshold.metric === METRIC_RATING) {
        return `${Number(value).toFixed(DECIMALS)} / ${RATING_SCALE_MAX}`;
      }

      return `${Number(value).toFixed(DECIMALS)}%`;
    },
    formatThreshold(value) {
      if (this.threshold.metric === METRIC_INCIDENTS) {
        return Number(value).toFixed(INCIDENTS_DECIMALS);
      }
      if (this.threshold.metric === METRIC_RATING) {
        return Number(value).toFixed(RATING_DECIMALS);
      }

      return `${Number(value).toFixed(PERCENT_DECIMALS)}%`;
    }
  }
};
</script>
